import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Plus, Trash2 } from 'lucide-react';

import { getQueueDetail } from '@/api/analytics';
import { useT } from '@/contexts/I18nContext';
import { spliceLine, type TypedLine } from '../constructor/lineModel';
import { defFor } from '../constructor/typedLines';
import { TypedLineForm } from '../constructor/TypedLineForm';
import { AnnotatableTimeChart } from '../chart/AnnotatableTimeChart';
import type { ChartSeries, ChartSegment, ChartPhoto, Domain } from '../chart/types';

// item 12 (round-2, Alexey 2026-09): bars were too aggregated to see momentary
// spikes — 1 minute is the finest bucket the backend supports (`interval_ms`
// floors to 60s server-side, services/services/api/analytics.py `_queue_overlay`),
// short of literally unaggregated per-frame data, which the chart/tooltip
// math here isn't built to consume. Was 5 min.
const QUEUE_BUCKET_MS = 60 * 1000;
const QUEUE_PHOTO_COUNT = 40; // ~1 per 15min over a working day — RackChart parity

interface QueueSeg extends ChartSegment {
  kind: 'longest' | 'event';
  secondCashier?: boolean;
}

/**
 * F3 — очередь и бар: one shared chart for the whole line group (dense
 * bars+cashier-steps, per Alexey's "annotate all at once without zooming").
 * `q_longest` behaves like a keypoint (chip-added, then drag its bounds —
 * never empty-drag-created, since only one can exist); `q_longqueue`'s
 * over-threshold events are drag-created (any empty-space drag on this chart
 * defaults to that type); `q_novisit` has no chart at all — reuses the plain
 * V2 form inline.
 */
export function QueueChartBlock({
  lines,
  zoneId,
  day,
  value,
  onChange,
  maxCashiers,
  disabled,
}: {
  lines: TypedLine[];
  zoneId: string;
  day: string;
  value: string;
  onChange: (bodyHtml: string) => void;
  maxCashiers?: number | null;
  disabled?: boolean;
}) {
  const { t } = useT();
  const longestLine = lines.find((l) => l.typedKind === 'q_longest') ?? null;
  const novisitLine = lines.find((l) => l.typedKind === 'q_novisit') ?? null;
  const longqueueLine = lines.find((l) => l.typedKind === 'q_longqueue') ?? null;

  const { data } = useSWR(
    zoneId ? ['rv-interactive-queue', zoneId, day] : null,
    // photoCount was 1 — the queue chart had no photo strip above it (item 14,
    // second half) because it only ever asked for a single photo for the
    // whole day. Match RackChart's density (PHOTO_COUNT) instead.
    () => getQueueDetail(zoneId, day, day, QUEUE_BUCKET_MS, { photoCount: QUEUE_PHOTO_COUNT }).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  const domainFull = useMemo<Domain | null>(() => {
    const wh = data?.working_hours;
    if (!wh) return null;
    const s = new Date(wh.start).getTime();
    const e = new Date(wh.end).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && e > s ? [s, e] : null;
  }, [data?.working_hours]);

  const queuePoints = useMemo(
    () => (data?.overlay ?? []).map((p) => ({ t: new Date(p.time).getTime(), v: p.queue })),
    [data?.overlay],
  );
  const cashierPoints = useMemo(
    () => (data?.overlay ?? []).map((p) => ({ t: new Date(p.time).getTime(), v: p.cashier_count })),
    [data?.overlay],
  );
  // item 14: queue_photos was fetched but never wired into the chart — no
  // strip rendered above it at all (independent of the photoCount bug above).
  const photos: ChartPhoto[] = useMemo(
    () =>
      (data?.queue_photos ?? [])
        .filter((p) => p.image_url)
        .map((p) => ({
          t: new Date(p.at).getTime(),
          url: p.image_url!,
          caption: p.captured_occupancy != null ? `${p.captured_occupancy} чел.` : undefined,
        })),
    [data?.queue_photos],
  );
  const series: ChartSeries[] = useMemo(
    () => [
      {
        id: 'queue',
        kind: 'bar',
        points: queuePoints,
        axis: 'left',
        color: '#f59e0b',
        label: t('reportValidation.interactive.queueLabel'),
        bucketMs: QUEUE_BUCKET_MS,
      },
      {
        id: 'cashiers',
        kind: 'step',
        points: cashierPoints,
        axis: 'right',
        color: '#6366f1',
        label: t('report.day.openCashiers'),
      },
    ],
    [queuePoints, cashierPoints, t],
  );

  const [segs, setSegs] = useState<QueueSeg[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const hasLongest = segs.some((s) => s.kind === 'longest');

  const pushLongest = (next: QueueSeg[]) => {
    if (!longestLine) return;
    const seg = next.find((s) => s.kind === 'longest');
    if (!seg) return; // removed → leave text as last written; validator can still use the V2 form
    const def = defFor('q_longest');
    const inRange = queuePoints.filter((p) => p.t >= seg.t_start && p.t <= seg.t_end);
    const people = inRange.length ? Math.max(...inRange.map((p) => p.v)) : 0;
    const minutes = Math.round((seg.t_end - seg.t_start) / 60000);
    onChange(
      spliceLine(value, longestLine, [
        def.serialize({ ...longestLine.fields, people, minutes }, longestLine.meta),
      ]),
    );
  };
  const pushLongqueue = (next: QueueSeg[]) => {
    if (!longqueueLine) return;
    const events = next.filter((s) => s.kind === 'event');
    const def = defFor('q_longqueue');
    onChange(
      spliceLine(value, longqueueLine, [
        def.serialize(
          {
            ...longqueueLine.fields,
            events: events.length,
            secondOn: events.some((s) => s.secondCashier),
            second: events.filter((s) => s.secondCashier).length,
          },
          longqueueLine.meta,
        ),
      ]),
    );
  };

  const updateSegs = (fn: (arr: QueueSeg[]) => QueueSeg[]) => {
    setSegs((prev) => {
      const next = fn(prev);
      pushLongest(next);
      pushLongqueue(next);
      return next;
    });
  };

  const sel = segs.find((s) => s.id === selectedId) ?? null;

  const readout = (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
      {sel ? (
        <>
          <span className="font-medium">
            {sel.kind === 'longest'
              ? t('reportValidation.tf.q_longest')
              : t('reportValidation.tf.q_longqueue')}
          </span>
          {sel.kind === 'event' && (
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={Boolean(sel.secondCashier)}
                onChange={(e) =>
                  updateSegs((arr) =>
                    arr.map((s) => (s.id === sel.id ? { ...s, secondCashier: e.target.checked } : s)),
                  )
                }
                className="size-3.5"
              />
              {t('reportValidation.tf.secondCashierOn')}
            </label>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              updateSegs((arr) => arr.filter((s) => s.id !== sel.id));
              setSelectedId(null);
            }}
            className="ml-auto rounded-md border border-border p-1 text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="size-3.5" />
          </button>
        </>
      ) : (
        <>
          {!hasLongest && longestLine && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                let peakT = domainFull?.[0] ?? 0;
                let peakV = -1;
                for (const p of queuePoints) {
                  if (p.v > peakV) {
                    peakV = p.v;
                    peakT = p.t;
                  }
                }
                const seg: QueueSeg = {
                  id: `longest-${peakT}`,
                  kind: 'longest',
                  t_start: peakT - 10 * 60000,
                  t_end: peakT + 10 * 60000,
                };
                updateSegs((arr) => [...arr, seg]);
                setSelectedId(seg.id);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-muted-foreground hover:border-primary hover:text-primary"
            >
              <Plus className="size-3" /> {t('reportValidation.tf.q_longest')}
            </button>
          )}
        </>
      )}
    </div>
  );

  if (!domainFull) return <div className="h-56 animate-pulse rounded-xl bg-muted" />;

  return (
    <div className="flex flex-col gap-3">
      <AnnotatableTimeChart
        series={series}
        photos={photos}
        domainFull={domainFull}
        height={260}
        annotateCreates="segment"
        segments={segs}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onSegmentCreate={(seg) => {
          const next: QueueSeg = { ...seg, kind: 'event' };
          updateSegs((arr) => [...arr, next]);
          setSelectedId(seg.id);
        }}
        onSegmentChange={(seg) =>
          updateSegs((arr) =>
            arr.map((s) => (s.id === seg.id ? { ...s, t_start: seg.t_start, t_end: seg.t_end } : s)),
          )
        }
        addSegmentLabel={t('reportValidation.interactive.addEvent')}
        readout={readout}
      />
      {novisitLine && (
        // item 7: its own card, shown once — its full text already appears
        // in the shared summary above (D1's "whole text first" pattern), so
        // the form itself hides its OWN preview box (hidePreview) instead of
        // showing that exact same line a second time.
        <div className="rounded-xl border border-border bg-card p-3">
          <TypedLineForm
            line={novisitLine}
            day={day}
            maxCashiers={maxCashiers}
            onApply={(row) => onChange(spliceLine(value, novisitLine, [row]))}
            onCancel={() => {}}
            hidePreview
          />
        </div>
      )}
    </div>
  );
}
